import {
  type UseQueryResult,
  useQueries,
  useQuery,
} from '@tanstack/react-query'
import { useMemo } from 'react'

import type { KnowledgeBaseDocument } from '@/lib/definitions/knowledgeBase'
import { buildKnowledgeBaseCatalogue } from '@/lib/misc/knowledgeBase'
import { buildKnowledgeBaseIndex } from '@/lib/misc/knowledgeBaseContent'
import {
  type KnowledgeBaseFile,
  KnowledgeBaseNotFoundError,
  KnowledgeBaseService,
} from '@/lib/services/KnowledgeBaseService'

const knowledgeBaseService = new KnowledgeBaseService()

const KNOWLEDGE_BASE_STALE_TIME = 5 * 60_000

export const knowledgeBaseDocumentQueryKey = (path: string) =>
  ['knowledgeBase', 'document', path] as const

const documentQuery = (path: string) => ({
  queryKey: knowledgeBaseDocumentQueryKey(path),
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    knowledgeBaseService.getDocument(path, signal),
  staleTime: KNOWLEDGE_BASE_STALE_TIME,
  retry: (failureCount: number, error: Error) =>
    !(error instanceof KnowledgeBaseNotFoundError) && failureCount < 2,
})

export const useKnowledgeBaseDocument = (path: string | null) =>
  useQuery({
    ...documentQuery(path ?? ''),
    enabled: Boolean(path),
  })

export const useKnowledgeBaseCatalogue = (enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: ['knowledgeBase', 'catalogue'],
    queryFn: ({ signal }) => knowledgeBaseService.getCatalogue(signal),
    staleTime: KNOWLEDGE_BASE_STALE_TIME,
    enabled,
  })

  const documents = useMemo(() => buildKnowledgeBaseCatalogue(data), [data])

  return { documents, isLoading: enabled && isLoading }
}

const combineDocuments =
  (documents: KnowledgeBaseDocument[]) =>
  (results: UseQueryResult<KnowledgeBaseFile, Error>[]) => ({
    sources: documents.flatMap((document, position) => {
      const markdown = results[position]?.data?.markdown
      return markdown ? [{ document, markdown }] : []
    }),
    isLoading: results.some((result) => result.isLoading),
  })

export const useKnowledgeBaseIndex = (enabled: boolean) => {
  const catalogue = useKnowledgeBaseCatalogue(enabled)
  const { sources, isLoading } = useQueries({
    queries: catalogue.documents.map((document) => ({
      ...documentQuery(document.path),
      enabled,
    })),
    combine: useMemo(
      () => combineDocuments(catalogue.documents),
      [catalogue.documents]
    ),
  })

  const index = useMemo(() => buildKnowledgeBaseIndex(sources), [sources])

  return { index, isLoading: enabled && (catalogue.isLoading || isLoading) }
}
